/**
 * 003-bank-cashflow — typed client for `/api/v1/bank` (contracts/bank-api.md).
 * Response types mirror the API; request bodies come from @armard/shared.
 * All query keys start with ['bank', …] so one invalidation refreshes every tab.
 */
import type { BankClassification, BankReviewBody } from '@armard/shared';
import { api } from '../services/api';

// ------------------------------------------------------------------ shared

export type Allocation = { kind: 'invoice' | 'expense'; documentId: string; amountCents: number; evidence: string };

export type Counterparty = {
  kind: string;
  name: string | null;
  iban: string | null;
  creditorId: string | null;
  mandateRef: string | null;
  reference: string;
  key: string;
};

export type SuggestionLevel = 'hoch' | 'mittel' | 'niedrig';

export type Suggestion = {
  id: string;
  transactionId: string;
  kind: 'document' | 'collective' | 'partial' | 'classification' | 'debit_return_pair';
  targets: {
    kind: 'invoice' | 'expense';
    documentId: string;
    amountCents: number;
    label: string;
    number: string | null;
    date: string | null;
    remainingCents: number;
    status: string;
    unreviewed: boolean;
  }[];
  classification?: string;
  partnerId?: string;
  pairedTransactionId?: string;
  supplierId?: string;
  categoryId?: string;
  level: SuggestionLevel;
  score: number;
  signals: { code: string; label: string }[];
  origin: 'engine' | 'rule';
  ruleId?: string;
  conflict?: boolean;
  bulkEligible: boolean;
};

export type MovementStatus = 'offen' | 'vorschlag' | 'bestaetigt' | 'ausgeblendet';

export type HistoryEntry = {
  at: string;
  actor: string;
  classification: string;
  evidence: string;
  allocations: Allocation[];
  partnerId?: string | null;
  pairedTransactionId?: string | null;
  suggestion?: { id: string; level: SuggestionLevel } | null;
  bulk?: boolean;
};

export type TransactionRow = {
  id: string;
  statementId: string;
  account: string;
  statementPeriod: { from: string; to: string } | null;
  ordinal: number;
  version: number;
  bookingDate: string;
  valueDate: string;
  amountCents: number;
  description: string;
  counterparty: Counterparty;
  classification: string;
  partnerId: string | null;
  pairedTransactionId: string | null;
  allocations: Allocation[];
  /** Confirmed allocations whose document was merged/withdrawn: kept in history, no longer counted. */
  inactiveAllocations?: Allocation[];
  /** Confirmed allocations whose document was corrected to the opposite payment direction: no longer counted. */
  directionMismatchAllocations?: Allocation[];
  unallocatedCents: number;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewEvidence: string | null;
  reviewHistory: HistoryEntry[];
  status: MovementStatus;
  suggestions: Suggestion[];
};

export type Partner = { id: string; displayName: string; ibans: string[]; active: boolean; version: number };

export type TransactionsResponse = {
  rows: TransactionRow[];
  counts: Record<MovementStatus, number>;
  accounts: string[];
  partners: { id: string; displayName: string; ibans: string[]; active: boolean }[];
  categories: { id: string; name: string }[];
};

export type CoverageResponse = {
  accounts: {
    account: string;
    label: string;
    statements: number;
    firstDate: string | null;
    lastDate: string | null;
    gaps: { from: string; to: string }[];
    months: { month: string; complete: boolean; openingCents: number | null; closingCents: number | null }[];
    quarters: { quarter: string; complete: boolean; openingCents: number | null; closingCents: number | null }[];
  }[];
};

export type PaymentRecord = {
  documentKind: 'invoice' | 'expense';
  documentId: string;
  source: 'bank' | 'manual';
  bookingDate: string;
  transactionId: string | null;
  amountCents: number;
  netShareCents: number;
  vatShareCents: number;
};

export type IstReport = {
  quarter: string;
  basis: 'paymentDate';
  coverage: { complete: boolean; gaps: { from: string; to: string }[] };
  documents: {
    kind: 'invoice' | 'expense';
    id: string;
    number: string | null;
    counterparty: string | null;
    invoiceDate: string | null;
    status: string;
    unreviewed: boolean;
    currency: string;
    /** Flagged as the tenant's own outgoing document and not yet resolved by a review: never summed. */
    outgoingFlag: boolean;
    grossCents: number;
    vatCents: number;
    standingInvoice: boolean;
    payments: PaymentRecord[];
    paidInQuarterCents: number;
    vatInQuarterCents: number;
    netInQuarterCents: number;
  }[];
  totals: {
    invoice: { gross: number; net: number; vat: number; count: number };
    expense: { gross: number; net: number; vat: number; count: number };
    manualOnly: { gross: number; vat: number; count: number };
    unreviewed: { count: number };
    foreignCurrency: { count: number };
    outgoingFlagged: { count: number };
  };
  gaps: {
    paidWithoutDocument: { transactionId: string; bookingDate: string; amountCents: number; counterparty: Counterparty | null }[];
    documentWithoutPayment: { kind: 'invoice' | 'expense'; id: string; number: string | null; invoiceDate: string | null; grossCents: number; currency: string }[];
    manualOnly: { kind: 'invoice' | 'expense'; id: string; number: string | null; paymentDate: string; grossCents: number; currency: string }[];
  };
};

export type Dismissed = null | { id: string; reason: string; by: string; at: string };

export type RecurringReport = {
  window: { from: string; to: string };
  groups: {
    key: string;
    counterpartyName: string | null;
    iban: string | null;
    interval: 'monthly' | 'quarterly' | 'yearly';
    typicalCents: number;
    occurrences: { transactionId: string; bookingDate: string; amountCents: number; reference: string }[];
    coveredCount: number;
    firstDate: string;
    lastDate: string;
    sumCents: number;
    supplier: { id: string; displayName: string } | null;
    dismissed: Dismissed;
    /** Occurrences whose receipt already exists as a suggestion (confirm it under Bankbuchungen). */
    pendingSuggestionCount?: number;
  }[];
  oneOff: {
    transactionId: string;
    bookingDate: string;
    amountCents: number;
    counterparty: Counterparty;
    supplier: { id: string; displayName: string } | null;
    dismissed: Dismissed;
    pendingSuggestion?: boolean;
  }[];
};

export type MonthBlocks = {
  month: string;
  businessIn: Record<string, number>;
  businessOut: Record<string, number>;
  partners: Record<string, { in: number; out: number }>;
  tax: number;
  transfer: number;
  unassigned: { cents: number; count: number };
};

export type BlocksPart = {
  blocks: { months: MonthBlocks[]; total: MonthBlocks };
  reconciliation: { blocksSumCents: number; bankNetCents: number; ok: true };
  categories: { id: string; name: string }[];
  partners: { id: string; displayName: string }[];
};

export type Rule = {
  id: string;
  name: string;
  counterpartyIban: string | null;
  counterpartyNameContains: string | null;
  referenceContains: string | null;
  direction: 'in' | 'out' | 'any';
  minCents: number | null;
  maxCents: number | null;
  outcomeClassification: string | null;
  outcomePartnerId: string | null;
  outcomeSupplierId: string | null;
  outcomeCategoryId: string | null;
  active: boolean;
  version: number;
  matchCount: number;
};

// ------------------------------------------------------------ US6 forecast

export type ForecastSource = 'recurring' | 'invoice' | 'planned';
export type ForecastItem = {
  date: string;
  amountCents: number;
  source: ForecastSource;
  sourceLabel: string;
  label: string;
  refId: string;
  overdue: boolean;
  interval?: 'monthly' | 'quarterly' | 'yearly';
};
export type ForecastPoint = { date: string; balanceCents: number };
export type RecurringForecastGroup = {
  groupKey: string;
  refId: string;
  label: string;
  interval: 'monthly' | 'quarterly' | 'yearly';
  amountCents: number;
  lastDate: string;
  count: number;
  ended: { id: string; from: string; reason: string; by: string; at: string } | null;
  nextDate: string | null;
  projectedCount: number;
};
export type ForecastResponse =
  | {
      ok: false;
      today: string;
      cutoff: string;
      missing: { account: string | null; from: string | null; to: string }[];
    }
  | {
      ok: true;
      today: string;
      anchor: { date: string; balanceCents: number; accounts: { account: string; balanceCents: number; lastStatementEnd: string }[] };
      endDate: string;
      includeExpected: boolean;
      days: { date: string; inCents: number; outCents: number; balanceCents: number; items: ForecastItem[] }[];
      firstNegative: ForecastPoint | null;
      lowest: ForecastPoint | null;
      endCents: number;
      totals: Record<ForecastSource, { count: number; cents: number }>;
      excludedExpected: { count: number; cents: number };
      includeLongOverdue: boolean;
      longOverdue: {
        included: boolean;
        count: number;
        cents: number;
        invoices: { id: string; label: string; dueDate: string; remainingCents: number }[];
      };
      recurringGroups: RecurringForecastGroup[];
      unmatchedEndings: { id: string; groupKey: string; label: string | null; amountCents: number | null; endedFrom: string; reason: string }[];
      notCounted: {
        plannedBeforeAnchor: ForecastItem[];
        plannedAfterEnd: ForecastItem[];
        invoicesWithoutDueDate: { id: string; number: string | null; remainingCents: number }[];
      };
    };
export type PlannedItem = {
  id: string;
  plannedDate: string;
  amountCents: number;
  label: string;
  categoryId: string | null;
  counterparty: string | null;
  source: 'manual';
  version: number;
};
export type PlannedItemBody = {
  plannedDate: string;
  amountCents: number;
  label: string;
  counterparty?: string | null;
  categoryId?: string | null;
};

// ------------------------------------------------------------------ keys

export const bankKeys = {
  all: ['bank'] as const,
  cashflow: (tenant: string | undefined, from: string, to: string, basis: string, account: string | undefined) =>
    ['bank', 'cashflow', tenant, from, to, basis, account ?? 'all'] as const,
  transactions: (tenant: string | undefined, from: string, to: string, account: string | undefined) =>
    ['bank', 'transactions', tenant, from, to, account ?? 'all'] as const,
  coverage: (tenant: string | undefined, from: string, to: string) => ['bank', 'coverage', tenant, from, to] as const,
  ist: (tenant: string | undefined, quarter: string, account: string | undefined) =>
    ['bank', 'ist', tenant, quarter, account ?? 'all'] as const,
  recurring: (tenant: string | undefined, from: string, to: string, includeDismissed: boolean) =>
    ['bank', 'recurring', tenant, from, to, includeDismissed] as const,
  partners: (tenant: string | undefined) => ['bank', 'partners', tenant] as const,
  forecast: (tenant: string | undefined, horizonDays: number, includeExpected: boolean, includeLongOverdue: boolean) =>
    ['bank', 'forecast', tenant, horizonDays, includeExpected, includeLongOverdue] as const,
  plannedItems: (tenant: string | undefined) => ['bank', 'planned-items', tenant] as const,
  rules: (tenant: string | undefined) => ['bank', 'rules', tenant] as const,
};

/**
 * Decision 2026-10-18: a confirmed match can set an own 001 invoice to paid (or
 * revert it), so after a review the invoice, dashboard and reminder views refetch too.
 */
export const reviewSideEffectKeys = [bankKeys.all, ['invoices'], ['dashboard'], ['reminders']] as const;

const qs = (params: Record<string, string | undefined | boolean>) =>
  new globalThis.URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)])
  ).toString();

// --------------------------------------------------------------- fetchers

export const bankApi = {
  transactions: (p: { from: string; to: string; account?: string }) =>
    api.get<TransactionsResponse>(`/bank/transactions?${qs(p)}`),
  review: (id: string, body: BankReviewBody) => api.patch<TransactionRow>(`/bank/transactions/${id}/review`, body),
  reject: (id: string, suggestionId: string, reason: string) =>
    api.post<void>(`/bank/transactions/${id}/reject`, { suggestionId, reason }),
  bulkReview: (items: { id: string; expectedVersion: number; suggestionId: string }[]) =>
    api.post<{ confirmed: number }>('/bank/transactions/review-bulk', { items }),
  coverage: (p: { from: string; to: string }) => api.get<CoverageResponse>(`/bank/coverage?${qs(p)}`),
  ist: (p: { quarter: string; account?: string }) => api.get<IstReport>(`/bank/quarters/ist?${qs(p)}`),
  istCsvUrl: (p: { quarter: string; account?: string }) => `${api.baseUrl}/bank/quarters/ist?${qs({ ...p, format: 'csv' })}`,
  recurring: (p: { from: string; to: string; includeDismissed: boolean }) =>
    api.get<RecurringReport>(`/bank/reports/recurring-without-receipt?${qs(p)}`),
  recurringCsvUrl: (p: { from: string; to: string; includeDismissed: boolean }) =>
    `${api.baseUrl}/bank/reports/recurring-without-receipt?${qs({ ...p, format: 'csv' })}`,
  dismiss: (body: { counterpartyKey?: string; transactionId?: string; reason: string }) =>
    api.post<{ id: string }>('/bank/reports/recurring-without-receipt/dismissals', body),
  undismiss: (id: string) => api.delete<unknown>(`/bank/reports/recurring-without-receipt/dismissals/${id}`),
  partners: () => api.get<Partner[]>('/bank/partners'),
  createPartner: (body: { displayName: string; ibans: string[] }) => api.post<Partner>('/bank/partners', body),
  updatePartner: (id: string, body: { expectedVersion: number; displayName?: string; ibans?: string[]; active?: boolean }) =>
    api.patch<Partner>(`/bank/partners/${id}`, body),
  rules: () => api.get<Rule[]>('/bank/rules'),
  createRule: (body: Record<string, unknown>) => api.post<Rule>('/bank/rules', body),
  forecast: (p: { horizonDays: number; includeExpected: boolean; includeLongOverdue: boolean }) =>
    api.get<ForecastResponse>(
      `/bank/forecast?${qs({ horizonDays: String(p.horizonDays), includeExpected: p.includeExpected, includeLongOverdue: p.includeLongOverdue })}`
    ),
  endRecurring: (body: { groupKey: string; label?: string; amountCents?: number | null; endedFrom: string; reason: string }) =>
    api.post<{ id: string }>('/bank/forecast/recurring-endings', body),
  resumeRecurring: (id: string) => api.delete<unknown>(`/bank/forecast/recurring-endings/${id}`),
  plannedItems: () => api.get<PlannedItem[]>('/bank/planned-items'),
  createPlannedItem: (body: PlannedItemBody) => api.post<PlannedItem>('/bank/planned-items', body),
  updatePlannedItem: (id: string, body: Partial<PlannedItemBody> & { expectedVersion: number }) =>
    api.patch<PlannedItem>(`/bank/planned-items/${id}`, body),
  deletePlannedItem: (id: string) => api.delete<void>(`/bank/planned-items/${id}`),
  originalUrl: (statementId: string) => `${api.baseUrl}/bank/statements/${statementId}/original`,
};

// ------------------------------------------------------------- formatting

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
export const money = (cents: number | null | undefined) => (cents === null || cents === undefined ? 'Nicht belegt' : EUR.format(cents / 100));
export const day = (iso: string | null | undefined) =>
  iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—';
export const ibanLabel = (iban: string | null | undefined) => (iban ? iban.replace(/(.{4})/g, '$1 ').trim() : '—');

export const CLASSIFICATION_LABELS: Record<BankClassification, string> = {
  unknown: 'Unklar',
  business: 'Geschäftlich',
  private_outlay: 'Private Auslage',
  reimbursement: 'Erstattung an Gesellschafter',
  transfer: 'Umbuchung',
  deposit: 'Einlage',
  withdrawal: 'Entnahme',
  tax_payment: 'Steuerzahlung',
  bank_fee: 'Bankentgelt',
  payout_income: 'Auszahlung Zahlungsdienstleister',
  debit_return: 'Rücklastschrift',
  private: 'Privat (aus Geschäftskonto)',
};
export const PARTNER_CLASSIFICATIONS = ['deposit', 'withdrawal', 'reimbursement', 'private'];

export const STATUS_LABELS: Record<MovementStatus, string> = {
  offen: 'Offen',
  vorschlag: 'Vorschlag',
  bestaetigt: 'Bestätigt',
  ausgeblendet: 'Ausgeblendet',
};

export const quarterOf = (iso: string) => `${iso.slice(0, 4)}-Q${Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1}`;
